import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import {
  EVIDENCE_COPY_CONFIRMED_CHAT_IDS_STORAGE_KEY,
  type EvidenceCopyConfirmationSurface,
} from '../../contracts';

/** Set to `true` to re-enable the EVI-1452 / SWR-88 PHI/PII copy attestation popover. */
export const IS_EVIDENCE_COPY_CONFIRMATION_ENABLED = false;

/**
 * State for the Evidence copy-to-clipboard PHI/PII confirmation popover.
 *
 * The popover gates Evidence "Copy answer" actions (Reply actions + user-notes block).
 * Carries the deferred copy action, the surface that triggered it, the chat id being
 * copied, and the anchor element to position the popover next to (SWR-88 / EVI-1452).
 *
 * Singleton: one popover is mounted globally and reads this atom; every Evidence copy
 * button writes into it on click.
 */
export type EvidenceCopyConfirmationModalState = {
  isOpen: boolean;
  /** Callback invoked when the user confirms — typically the actual clipboard write. */
  onConfirm: (() => void) | null;
  /** Chat ID being copied; persisted on confirm so the popover is only shown once per chat. */
  chatId: string | null;
  /**
   * Backend message ID (msg_*) of the assistant response being copied, when the
   * triggering surface knows it (reply actions). Null for surfaces with no single
   * owning response (user-notes block, coding-case thread). Attributes the copy to
   * the producing model.
   */
  messageId: string | null;
  /** Surface that initiated the gate (for analytics segmentation). */
  surface: EvidenceCopyConfirmationSurface | null;
  /**
   * Anchor element the popover should position against. Stored directly so that
   * the singleton popover can attach to whichever copy button triggered it without
   * needing to know about per-instance refs.
   */
  anchorEl: HTMLElement | null;
};

export const evidenceCopyConfirmationModalAtom = atom<EvidenceCopyConfirmationModalState>(
  {
    isOpen: false,
    onConfirm: null,
    chatId: null,
    messageId: null,
    surface: null,
    anchorEl: null,
  }
);

/**
 * SSR-safe JSON storage so reads during server render fall back to the initial value
 * and writes are no-ops (window does not exist).
 */
const createSsrSafeStorage = <T>() =>
  createJSONStorage<T>(() => {
    if (typeof window === 'undefined') {
      return {
        getItem: () => null,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
    }
    return window.localStorage;
  });

/**
 * Set of Evidence chat IDs the user has already confirmed PHI/PII attestation for.
 * Stored as a `Record<chatId, true>` for O(1) lookup; persisted across reloads so
 * the popover does not re-show after a page refresh inside the same chat.
 *
 * Cleared on full logout (see `clearLogoutStorage`).
 */
export const confirmedEvidenceCopyChatIdsAtom = atomWithStorage<Record<string, true>>(
  EVIDENCE_COPY_CONFIRMED_CHAT_IDS_STORAGE_KEY,
  {},
  createSsrSafeStorage<Record<string, true>>()
);
